/* Iter 92 — generic section renderers for the dynamic landing page.
 * Each section type accepts a flat `content` object (already
 * lang-resolved by the parent). All visual logic stays here so the
 * admin CMS doesn't have to know about Tailwind / Lucide. */
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Swords, Sword, Trophy, ShoppingBag,
  Music, Smartphone, ShieldCheck, Sparkles, Crown,
  Clock, Flag, Users, ChevronRight,
} from "lucide-react";

// Icon registry — admins can pick from these by name
export const ICON_MAP = {
  book: BookOpen, sword: Sword, swords: Swords,
  trophy: Trophy, shop: ShoppingBag, music: Music,
  mobile: Smartphone, shield: ShieldCheck, sparkles: Sparkles,
  crown: Crown, clock: Clock, flag: Flag, users: Users,
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          obs.unobserve(el);
        }
      }),
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        opacity: 0,
        transform: "translateY(28px)",
        transition: `opacity 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
      className={className}
    >
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-heading uppercase tracking-[0.3em] text-[#cfb53b]/70 mb-3">{children}</p>
);

const Lede = ({ children, align = "left" }) => (
  <p className={`text-base md:text-lg font-body leading-relaxed text-[#cbd5e1]/85 max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {children}
  </p>
);

const FeatureCard = ({ icon: Icon, title, children, className = "" }) => (
  <div className={`group relative bg-[#15151a]/70 backdrop-blur-sm border border-[#cfb53b]/15 hover:border-[#cfb53b]/55 hover:shadow-[0_0_24px_rgba(207,181,59,0.18)] p-6 md:p-7 transition-all duration-500 hover:-translate-y-0.5 ${className}`}>
    <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[#cfb53b]/30" />
    <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[#cfb53b]/30" />
    {Icon && <Icon className="text-[#cfb53b] mb-4" size={26} strokeWidth={1.5} />}
    <h3 className="font-heading text-lg md:text-xl text-[#cfb53b] mb-2 tracking-wide">{title}</h3>
    <p className="font-body text-sm md:text-[15px] text-[#cbd5e1]/80 leading-relaxed">{children}</p>
  </div>
);

const Image = ({ src, alt, caption }) => {
  const [failed, setFailed] = React.useState(false);
  if (!src) return null;
  return (
    <figure className="relative">
      <div className="relative border border-[#cfb53b]/30 p-1 bg-gradient-to-br from-[#cfb53b]/10 via-transparent to-[#cfb53b]/5 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[#cfb53b]" />
        <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-[#cfb53b]" />
        <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-[#cfb53b]" />
        <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-[#cfb53b]" />
        {/* Iter 92.2 — visible placeholder when the image URL is broken
            (e.g. stale `/landing/uploads/...` URLs from before iter 92.1).
            Without this the broken <img> collapses to 0 height and the
            user just sees an empty golden frame, which is confusing. */}
        {failed ? (
          <div className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 bg-[#1a1a1d]/60 text-[#cfb53b]/50 text-center p-6">
            <Sparkles size={32} strokeWidth={1.2} className="opacity-50" />
            <p className="font-heading text-xs uppercase tracking-[0.2em]">Immagine non disponibile</p>
            <p className="font-body text-[10px] text-[#cbd5e1]/40">
              Ricarica l'immagine dal pannello admin → Landing
            </p>
          </div>
        ) : (
          <img
            src={src}
            alt={alt || ""}
            loading="lazy"
            onError={() => setFailed(true)}
            className="w-full h-auto block"
          />
        )}
      </div>
      {caption && (
        <figcaption className="mt-4 text-center text-xs font-heading uppercase tracking-[0.25em] text-[#cfb53b]/60">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

// ─── Renderers per type ─────────────────────────────────────────────────
const HeroSection = ({ content }) => (
  <section className="relative min-h-screen flex items-center justify-center px-4 pt-24 pb-16 overflow-hidden">
    {content.background_image && (
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${content.background_image})` }} />
    )}
    <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/85 via-[#0a0a0c]/75 to-[#0a0a0c]" />
    <div className="absolute inset-0 opacity-50" style={{ background: "radial-gradient(ellipse 80% 60% at center, rgba(207,181,59,0.18) 0%, transparent 60%)" }} />
    <div className="relative z-10 text-center max-w-4xl">
      <Reveal>
        {content.eyebrow && <p className="font-heading text-[10px] md:text-xs uppercase tracking-[0.5em] text-[#cfb53b]/70 mb-6">{content.eyebrow}</p>}
        {content.title && (
          <h1 className="font-heading font-bold text-5xl md:text-7xl lg:text-8xl tracking-tight text-[#cfb53b] drop-shadow-[0_4px_30px_rgba(207,181,59,0.35)] mb-8 leading-[1.05] whitespace-pre-line">
            {content.title}
          </h1>
        )}
        {content.subtitle && (
          <p className="font-body italic text-lg md:text-2xl text-[#cbd5e1]/90 max-w-2xl mx-auto leading-relaxed mb-12 whitespace-pre-line">
            {content.subtitle}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          {content.cta_primary_label && (
            <Link
              to={content.cta_primary_href || "/auth"}
              className="group font-heading text-sm md:text-base uppercase tracking-[0.25em] px-10 py-4 bg-[#cfb53b] text-[#0a0a0c] hover:bg-[#e5cc5b] hover:shadow-[0_0_30px_rgba(207,181,59,0.55)] transition-all duration-300 inline-flex items-center gap-2"
            >
              {content.cta_primary_label}
              <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
          {content.cta_secondary_label && (
            <Link
              to={content.cta_secondary_href || "/"}
              className="font-heading text-sm md:text-base uppercase tracking-[0.25em] px-10 py-4 border border-[#cfb53b]/60 text-[#cfb53b]/90 hover:border-[#cfb53b] hover:bg-[#cfb53b]/10 transition-all"
            >
              {content.cta_secondary_label}
            </Link>
          )}
        </div>
      </Reveal>
    </div>
  </section>
);

const SplitSection = ({ content, dark }) => {
  const imageOnRight = (content.image_position || "right") === "right";
  const textCol = (
    <Reveal className="md:col-span-7">
      {content.eyebrow && <SectionLabel>{content.eyebrow}</SectionLabel>}
      {(content.title || content.subtitle) && (
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#f8fafc] tracking-tight mb-5">
          {content.title}
          {content.subtitle && (<><br/><span className="text-[#cfb53b]">{content.subtitle}</span></>)}
        </h2>
      )}
      {content.body && <Lede>{content.body}</Lede>}
      {Array.isArray(content.stats) && content.stats.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mt-10 max-w-md">
          {content.stats.map((stat, i) => (
            <div key={i}>
              <p className="font-heading text-3xl text-[#cfb53b]">{stat.value}</p>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-[#cbd5e1]/60 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      )}
    </Reveal>
  );
  const imageCol = (
    <Reveal className="md:col-span-5" delay={150}>
      <Image src={content.image} caption={content.image_caption} />
    </Reveal>
  );
  return (
    <section className={`relative px-4 py-20 md:py-28 ${dark ? "bg-[#0a0a0c]" : "bg-[#0f0e10]"}`}>
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-center">
        {imageOnRight ? <>{textCol}{imageCol}</> : <>{imageCol}{textCol}</>}
      </div>
    </section>
  );
};

const FeatureGridSection = ({ content, dark }) => (
  <section className={`relative px-4 py-20 md:py-28 ${dark ? "bg-[#0a0a0c]" : "bg-[#0f0e10]"}`}>
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <div className="text-center mb-14">
          {content.eyebrow && <SectionLabel>{content.eyebrow}</SectionLabel>}
          {content.title && (
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#f8fafc] tracking-tight mb-5 text-center whitespace-pre-line">
              {content.title}
            </h2>
          )}
          {content.body && <Lede align="center">{content.body}</Lede>}
        </div>
      </Reveal>
      {Array.isArray(content.cards) && content.cards.length > 0 && (
        <div className={`grid grid-cols-1 md:grid-cols-${Math.min(content.cards.length, 3)} gap-4 md:gap-6`}>
          {content.cards.map((card, i) => {
            const Icon = ICON_MAP[card.icon] || Sparkles;
            return (
              <Reveal key={i} delay={i * 100}>
                <FeatureCard icon={Icon} title={card.title}>{card.body}</FeatureCard>
              </Reveal>
            );
          })}
        </div>
      )}
      {content.image && (
        <Reveal delay={250} className="mt-10">
          <Image src={content.image} caption={content.image_caption} />
        </Reveal>
      )}
    </div>
  </section>
);

const CtaSection = ({ content }) => (
  <section className="relative px-4 py-32 overflow-hidden bg-[#0a0a0c]">
    {content.background_image && (
      <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${content.background_image})` }} />
    )}
    <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/95 via-[#0a0a0c]/85 to-[#0a0a0c]" />
    <div className="relative max-w-3xl mx-auto text-center">
      <Reveal>
        <Users className="text-[#cfb53b] mx-auto mb-6" size={32} strokeWidth={1.5} />
        {content.title && (
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#cfb53b] mb-6 leading-tight tracking-tight drop-shadow-[0_4px_30px_rgba(207,181,59,0.3)] whitespace-pre-line">
            {content.title}
          </h2>
        )}
        {content.body && (
          <p className="font-body italic text-lg md:text-xl text-[#cbd5e1]/85 max-w-xl mx-auto mb-10">{content.body}</p>
        )}
        {content.button_label && (
          <Link
            to={content.button_href || "/auth"}
            className="group font-heading text-base uppercase tracking-[0.25em] px-12 py-4 bg-[#cfb53b] text-[#0a0a0c] hover:bg-[#e5cc5b] hover:shadow-[0_0_40px_rgba(207,181,59,0.6)] transition-all duration-300 inline-flex items-center gap-3"
          >
            {content.button_label}
            <Sparkles size={16} className="group-hover:rotate-180 transition-transform duration-700" />
          </Link>
        )}
      </Reveal>
    </div>
  </section>
);

const HowToPlaySection = ({ content, dark }) => (
  <section className={`relative px-4 py-20 md:py-28 ${dark ? "bg-[#0a0a0c]" : "bg-[#0f0e10]"}`}>
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <div className="text-center mb-14">
          {content.eyebrow && <SectionLabel>{content.eyebrow}</SectionLabel>}
          {content.title && (
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-[#f8fafc] tracking-tight mb-5 text-center">
              {content.title}
            </h2>
          )}
          {content.body && <Lede align="center">{content.body}</Lede>}
        </div>
      </Reveal>
      {Array.isArray(content.steps) && content.steps.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 relative">
          {content.steps.map((step, i) => {
            const Icon = ICON_MAP[step.icon] || Sparkles;
            return (
              <Reveal key={i} delay={i * 90} className="h-full">
                <div className="relative h-full bg-[#15151a]/80 border border-[#cfb53b]/20 hover:border-[#cfb53b]/60 p-5 rounded-lg flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(207,181,59,0.15)] group">
                  <div className="absolute -top-3 left-4 px-2 py-0.5 bg-[#cfb53b] text-[#0a0a0c] font-heading font-bold text-[10px] uppercase tracking-wider rounded">
                    0{i + 1}
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#cfb53b]/10 border border-[#cfb53b]/30 flex items-center justify-center my-3 group-hover:scale-110 group-hover:bg-[#cfb53b]/20 transition-all">
                    <Icon size={22} className="text-[#cfb53b]" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-[#cfb53b] mb-2 leading-tight">
                    {step.title}
                  </h3>
                  <p className="font-body text-xs text-[#cbd5e1]/75 leading-relaxed flex-1">
                    {step.body}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      )}
    </div>
  </section>
);

const SECTION_RENDERERS = {
  hero: HeroSection,
  split: SplitSection,
  feature_grid: FeatureGridSection,
  how_to_play: HowToPlaySection,
  cta: CtaSection,
};

export default function LandingSection({ section, lang, dark }) {
  const Renderer = SECTION_RENDERERS[section.type];
  if (!Renderer) return null;
  const content = section.content?.[lang] || section.content?.it || section.content?.en || {};
  return <Renderer content={content} dark={dark} />;
}

// Section type metadata used by the admin editor (defaults for new
// sections + which fields exist per type). Kept here so the editor
// stays in sync with renderers as types evolve.
export const SECTION_DEFAULTS = {
  hero: {
    eyebrow: "", title: "", subtitle: "",
    cta_primary_label: "", cta_primary_href: "/auth",
    cta_secondary_label: "", cta_secondary_href: "/",
    background_image: "",
  },
  split: {
    eyebrow: "", title: "", subtitle: "", body: "",
    stats: [], image: "", image_caption: "", image_position: "right",
  },
  feature_grid: {
    eyebrow: "", title: "", body: "",
    cards: [{ icon: "sparkles", title: "", body: "" }],
    image: "", image_caption: "",
  },
  cta: {
    title: "", body: "", button_label: "", button_href: "/auth",
    background_image: "",
  },
};

export const SECTION_TYPE_LABELS = {
  hero: "Hero (titolo principale)",
  split: "Split (testo + immagine)",
  feature_grid: "Griglia card",
  cta: "Call to Action",
};
